import React, { useState, useEffect, useMemo, useRef } from 'react';
import { API_URL, apiFetch } from '../services/api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
    LineChart, Line, AreaChart, Area, ScatterChart, Scatter, ZAxis
} from 'recharts';
import {
    ChevronLeftIcon, ChartBarIcon, DocumentTextIcon,
    LightBulbIcon, ArrowTrendingUpIcon, CalendarIcon,
    ChevronDownIcon, StarIcon, ExclamationTriangleIcon,
    FireIcon, RocketLaunchIcon, BeakerIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface TopicHistoryItem {
    id: string;
    examType: 'TYT' | 'AYT' | 'YDT';
    lesson: string;
    topic: string;
    history: Record<string, number>;
    totalLast7Years: number;
    average: number;
    importanceScore: number;
}

interface TopicHistoryViewProps {
    studentGrade?: string;
    onBack?: () => void;
}

const TYT_LESSONS = ['Türkçe', 'Matematik', 'Geometri', 'Fizik', 'Kimya', 'Biyoloji', 'Tarih', 'Coğrafya', 'Felsefe', 'Din Kültürü'];
const AYT_LESSONS = ['Matematik', 'Geometri', 'Edebiyat', 'Tarih-1', 'Coğrafya-1', 'Tarih-2', 'Coğrafya-2', 'Fizik', 'Kimya', 'Biyoloji', 'Felsefe', 'Psikoloji', 'Sosyoloji', 'Mantık', 'Din Kültürü'];
const YEARS = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];

export const TopicHistoryView: React.FC<TopicHistoryViewProps> = ({ studentGrade, onBack }) => {
    const [selectedExamType, setSelectedExamType] = useState<'TYT' | 'AYT' | 'YDT'>(
        studentGrade === '8. Sınıf' ? 'TYT' : 'TYT' // Default to TYT
    );
    const [selectedLesson, setSelectedLesson] = useState<string>('Türkçe');
    const [allData, setAllData] = useState<TopicHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const analysisRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedExamType === 'TYT') {
            setSelectedLesson(TYT_LESSONS[0]);
        } else if (selectedExamType === 'AYT') {
            setSelectedLesson(AYT_LESSONS[0]);
        } else if (selectedExamType === 'YDT') {
            setSelectedLesson('YDT');
        }
    }, [selectedExamType]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`${API_URL}/api/topic-history`);
            if (!response.ok) throw new Error('Veri yüklenemedi');
            const data = await response.json();
            setAllData(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        return allData.filter(item =>
            item.examType === selectedExamType &&
            item.lesson === selectedLesson
        ).sort((a, b) => b.totalLast7Years - a.totalLast7Years);
    }, [allData, selectedExamType, selectedLesson]);

    // Statistics Calculations
    const stats = useMemo(() => {
        if (filteredData.length === 0) return null;

        // 1. Banko Konular (Every year >= 1)
        const banko = filteredData.filter(item => {
            return YEARS.every(year => (item.history && (item.history[year] || 0) > 0));
        });

        // 2. En Çok Soru Çıkanlar (Top 3)
        const topTopics = [...filteredData].sort((a, b) => b.totalLast7Years - a.totalLast7Years).slice(0, 3);

        // 3. Trend Konular (Increase in last 3 years average vs all years average)
        const trendTopics = filteredData.filter(item => {
            const recentAvg = ((item.history['2025'] || 0) + (item.history['2024'] || 0) + (item.history['2023'] || 0)) / 3;
            return recentAvg > item.average;
        }).sort((a, b) => {
            const aRecent = ((a.history['2025'] || 0) + (a.history['2024'] || 0) + (a.history['2023'] || 0)) / 3;
            const bRecent = ((b.history['2025'] || 0) + (b.history['2024'] || 0) + (b.history['2023'] || 0)) / 3;
            return bRecent - aRecent;
        }).slice(0, 3);

        return { banko, topTopics, trendTopics };
    }, [filteredData]);

    const getStabilityScore = (history: Record<string, number>) => {
        const yearsWithQuestions = Object.values(history).filter(count => count > 0).length;
        return (yearsWithQuestions / YEARS.length) * 100;
    };

    const getPriorityTopic = () => {
        if (!filteredData.length) return null;
        return [...filteredData].sort((a, b) => b.importanceScore - a.importanceScore)[0];
    };

    const getRisingTopics = () => {
        return filteredData.filter(topic => {
            const recentAvg = ((topic.history['2025'] || 0) + (topic.history['2024'] || 0) + (topic.history['2023'] || 0)) / 3;
            const trend = recentAvg - topic.average;
            return trend > 0.01; // Only show strictly positive trends
        }).sort((a, b) => {
            const aRecent = ((a.history['2025'] || 0) + (a.history['2024'] || 0) + (a.history['2023'] || 0)) / 3;
            const bRecent = ((b.history['2025'] || 0) + (b.history['2024'] || 0) + (b.history['2023'] || 0)) / 3;
            return (bRecent - b.average) - (aRecent - a.average);
        }).slice(0, 3);
    };

    const priorityTopic = getPriorityTopic();
    const risingTopics = getRisingTopics();

    const [activeTopic, setActiveTopic] = useState<string | null>(null);

    useEffect(() => {
        if (filteredData.length > 0 && !activeTopic) {
            setActiveTopic(filteredData[0].topic);
        }
    }, [filteredData, activeTopic]);

    const topicChartData = useMemo(() => {
        const topicData = filteredData.find(t => t.topic === activeTopic);
        if (!topicData) return YEARS.map(year => ({ year, count: 0 }));

        return YEARS.map(year => ({
            year,
            count: topicData.history[year] || 0,
        }));
    }, [filteredData, activeTopic]);


    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 animate-pulse">
            <div className="w-16 h-16 border-4 border-[#2b2bee] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-slate-500 dark:text-slate-400 font-medium">Veriler hazırlanıyor...</p>
        </div>
    );

    return (
        <div className="flex flex-col gap-8 animate-fade-in pb-20">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                        <span className="p-2 bg-[#2b2bee]/20 rounded-xl text-[#2b2bee]">📊</span>
                        Çıkmış Soru <span className="text-[#2b2bee]">Dağılımı</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">Son 7 yılın ÖSYM verilerini detaylıca analiz et.</p>
                </div>

                {/* Selectors */}
                <div className="flex flex-wrap gap-3">
                    <div className="flex bg-slate-100 dark:bg-[#111122]/80 backdrop-blur-md p-1 rounded-2xl border border-slate-200 dark:border-white/5">
                        {['TYT', 'AYT', 'YDT'].map((type) => (
                            <button
                                key={type}
                                onClick={() => {
                                    setSelectedExamType(type as any);
                                    setActiveTopic(null); // Reset active topic on exam change
                                }}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${selectedExamType === type
                                    ? 'bg-[#2b2bee] text-white shadow-lg shadow-[#2b2bee]/25'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    <div className="relative group">
                        <select
                            value={selectedLesson}
                            onChange={(e) => {
                                setSelectedLesson(e.target.value);
                                setActiveTopic(null); // Reset active topic on lesson change
                            }}
                            title="Ders Seçimi"
                            className="appearance-none bg-slate-100 dark:bg-[#111122]/80 backdrop-blur-md border border-slate-200 dark:border-white/5 text-slate-900 dark:text-white px-6 py-3 pr-12 rounded-2xl font-bold cursor-pointer hover:border-slate-300 dark:hover:border-white/20 transition-all outline-none"
                        >
                            {(selectedExamType === 'TYT' ? TYT_LESSONS :
                                selectedExamType === 'AYT' ? AYT_LESSONS : ['YDT']).map(lesson => (
                                    <option key={lesson} value={lesson} className="bg-white dark:bg-[#111122]">{lesson}</option>
                                ))}
                        </select>
                        <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none group-hover:text-slate-800 dark:group-hover:text-white transition-colors" />
                    </div>
                </div>
            </div>

            {/* Dashboard Hero Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
                {/* Main Insight Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lg:col-span-8 p-8 rounded-[2rem] bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600 text-white relative overflow-hidden shadow-2xl shadow-indigo-500/20"
                >
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
                                En Yüksek Potansiyelli Konu
                            </span>
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                            {priorityTopic?.topic}
                        </h2>
                        <p className="text-white/80 text-lg max-w-xl mb-8 font-medium">
                            {selectedLesson} dersinde bu konu netlerini en hızlı artıracak kilit nokta. Son yıllardaki istikrarı ve soru ağırlığı ile stratejik önceliğin olmalı.
                        </p>
                        <div className="flex flex-wrap gap-6">
                            <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                                <div className="text-white/60 text-xs font-bold uppercase mb-1">Önem Skoru</div>
                                <div className="text-2xl font-black">{priorityTopic?.importanceScore}/100</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                                <div className="text-white/60 text-xs font-bold uppercase mb-1">Yıllık Ort.</div>
                                <div className="text-2xl font-black">{priorityTopic?.average.toFixed(1)} Soru</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10">
                                <div className="text-white/60 text-xs font-bold uppercase mb-1">İstikrar</div>
                                <div className="text-2xl font-black">%{priorityTopic ? getStabilityScore(priorityTopic.history).toFixed(0) : 0}</div>
                            </div>
                        </div>
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[150%] bg-gradient-to-l from-white/10 to-transparent rotate-12 blur-3xl pointer-events-none" />
                    <RocketLaunchIcon className="absolute bottom-6 right-8 w-32 h-32 text-white/10 -rotate-12 pointer-events-none" />
                </motion.div>

                {/* Growth Trends Card */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="lg:col-span-4 p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl relative overflow-hidden"
                >
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <FireIcon className="w-6 h-6 text-orange-500" />
                            Trend Olanlar
                        </h3>
                        <span className="text-xs font-bold text-slate-400">Son 3 Yıl</span>
                    </div>
                    <div className="space-y-4">
                        {risingTopics.map((topic, idx) => (
                            <div
                                key={topic.topic}
                                onClick={() => setActiveTopic(topic.topic)}
                                className="flex items-center gap-4 group cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 p-2 rounded-xl transition-colors"
                            >
                                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/10 flex items-center justify-center text-orange-600 font-bold shrink-0">
                                    {idx + 1}
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-slate-800 dark:text-slate-200 group-hover:text-[#2b2bee] transition-colors line-clamp-1">
                                        {topic.topic}
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1">
                                        <ArrowTrendingUpIcon className="w-3 h-3" />
                                        Artış Eğiliminde
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-black text-slate-900 dark:text-white">
                                        +{((((topic.history['2025'] || 0) + (topic.history['2024'] || 0) + (topic.history['2023'] || 0)) / 3) - topic.average).toFixed(1)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button 
                        onClick={() => analysisRef.current?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-full mt-8 py-4 rounded-2xl bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 text-sm font-bold hover:bg-slate-100 dark:hover:bg-white/10 transition-all border border-slate-200 dark:border-white/5"
                    >
                        Tüm Analizleri Gör
                    </button>
                </motion.div>
            </div>

            {/* Analysis Grid */}
            <div ref={analysisRef} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Visualizations Column */}
                <div className="lg:col-span-8 space-y-8">
                    {/* Topic Frequency Ranking */}
                    <div className="p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Konu Ağırlık Analizi</h3>
                                <p className="text-sm text-slate-400 mt-1">Ders genelindeki toplam soru yoğunluğu karşılaştırması</p>
                            </div>
                            <ChartBarIcon className="w-6 h-6 text-[#2b2bee]" />
                        </div>
                        <div className="h-[500px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    layout="vertical"
                                    data={filteredData.slice(0, 20)}
                                    margin={{ left: 160, right: 30, top: 0, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(148, 163, 184, 0.1)" />
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="topic"
                                        type="category"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }}
                                        width={150}
                                        tickFormatter={(value) => value.length > 28 ? `${value.substring(0, 25)}...` : value}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(43, 43, 238, 0.05)' }}
                                        contentStyle={{
                                            backgroundColor: '#111122',
                                            borderRadius: '16px',
                                            border: 'none',
                                            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
                                        }}
                                        itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                                    />
                                    <Bar
                                        dataKey="totalLast7Years"
                                        radius={[0, 8, 8, 0]}
                                        onClick={(data) => setActiveTopic(data.topic)}
                                        cursor="pointer"
                                        label={{ position: 'right', fill: '#94a3b8', fontSize: 10, fontWeight: 'bold' }}
                                    >
                                        {filteredData.slice(0, 20).map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={activeTopic === entry.topic ? '#2b2bee' : '#6366f1'}
                                                fillOpacity={activeTopic === entry.topic ? 1 : 0.4}
                                                className="transition-all duration-300"
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Distribution Chart Card */}
                    <div className="p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {activeTopic || 'Konu'} Analizi
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">Yıllara göre soru sayısı değişimi</p>
                            </div>
                            <div className="flex gap-2">
                                <BeakerIcon className="w-6 h-6 text-[#2b2bee]" />
                            </div>
                        </div>
                        <div className="h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={topicChartData}>
                                    <defs>
                                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2b2bee" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#2b2bee" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148, 163, 184, 0.1)" />
                                    <XAxis
                                        dataKey="year"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 600 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 600 }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#111122',
                                            borderRadius: '16px',
                                            border: 'none',
                                            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
                                        }}
                                        itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        stroke="#2b2bee"
                                        strokeWidth={4}
                                        fillOpacity={1}
                                        fill="url(#colorCount)"
                                        animationDuration={2000}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Topic Bubble Chart */}
                    <div className="p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Etki Matrisi</h3>
                                <p className="text-sm text-slate-400 mt-1">Önem (Y) vs Sıklık (X) Karşılaştırması</p>
                            </div>
                        </div>
                        <div className="h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                                    <XAxis type="number" dataKey="average" name="Sıklık" unit=" Soru" axisLine={false} tick={{ fill: '#94a3b8' }} />
                                    <YAxis type="number" dataKey="importanceScore" name="Önem" unit=" Puan" axisLine={false} tick={{ fill: '#94a3b8' }} />
                                    <ZAxis type="number" dataKey="totalLast7Years" range={[100, 1000]} name="Toplam Soru" />
                                    <Tooltip cursor={{ strokeDasharray: '3 3' }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0].payload;
                                                return (
                                                    <div className="bg-[#111122] p-4 rounded-2xl shadow-2xl border border-white/5">
                                                        <p className="text-white font-bold mb-2">{data.topic}</p>
                                                        <div className="space-y-1">
                                                            <p className="text-slate-400 text-xs">Önem: {data.importanceScore}</p>
                                                            <p className="text-slate-400 text-xs">Ortalama: {data.average.toFixed(1)} soru</p>
                                                            <p className="text-slate-400 text-xs">Toplam: {data.totalLast7Years} soru</p>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Scatter name="Konular" data={filteredData}>
                                        {filteredData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.importanceScore > 80 ? '#2b2bee' : '#6366f1'} />
                                        ))}
                                    </Scatter>
                                </ScatterChart>
                            </ResponsiveContainer>
                        </div>
                    </div>


                    {/* Topic Heatmap */}
                    <div className="p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Isı Haritası (Heatmap)</h3>
                                <p className="text-sm text-slate-400 mt-1">Konuların yıllara göre soru yoğunluğu</p>
                            </div>
                        </div>
                        <div className="overflow-x-auto pb-4 custom-scrollbar">
                            <div className="min-w-[700px]">
                                <div className="flex mb-4">
                                    <div className="w-[180px] shrink-0"></div>
                                    {YEARS.map(year => (
                                        <div key={year} className="flex-1 text-center text-[10px] font-black text-slate-400 uppercase tracking-tighter">{year}</div>
                                    ))}
                                </div>
                                <div className="space-y-1.5">
                                    {filteredData.slice(0, 20).map(topic => (
                                        <div key={topic.topic} className="flex items-center h-8 group">
                                            <div className="w-[180px] shrink-0 text-[11px] font-bold text-slate-600 dark:text-slate-400 line-clamp-1 pr-4 group-hover:text-[#2b2bee] transition-colors">{topic.topic}</div>
                                            {YEARS.map(year => {
                                                const count = topic.history[year] || 0;
                                                const intensity = Math.min(1, count / 4);
                                                return (
                                                    <div
                                                        key={`${topic.topic}-${year}`}
                                                        onClick={() => setActiveTopic(topic.topic)}
                                                        className={`flex-1 h-full mx-0.5 rounded-sm transition-all hover:scale-110 relative cursor-pointer ${activeTopic === topic.topic ? 'ring-2 ring-white ring-offset-1 z-10 scale-105' : ''}`}
                                                        style={{
                                                            backgroundColor: count > 0 ? `rgba(43, 43, 238, ${0.1 + intensity * 0.9})` : 'rgba(148, 163, 184, 0.05)',
                                                            border: count > 0 ? '1px solid rgba(43, 43, 238, 0.2)' : 'none'
                                                        }}
                                                        title={`${year}: ${count} Soru`}
                                                    >
                                                        {count > 0 && (
                                                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-white drop-shadow-sm">
                                                                {count}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vertical Topic List Column */}
                <div className="lg:col-span-4 lg:relative">
                    <div className="lg:sticky lg:top-8 p-8 rounded-[2rem] bg-white dark:bg-[#111122] border border-slate-200 dark:border-white/5 shadow-xl flex flex-col h-[700px] lg:h-[1050px]">
                        <div className="flex items-center justify-between mb-6 shrink-0">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Konu Kartları</h3>
                            <span className="bg-[#2b2bee]/10 text-[#2b2bee] px-3 py-1 rounded-full text-xs font-bold">{filteredData.length} Konu</span>
                        </div>
                        <div className="overflow-y-auto pr-2 space-y-4 custom-scrollbar flex-1">
                            {filteredData.map((item) => (
                                <div
                                    key={item.topic}
                                    onClick={() => setActiveTopic(item.topic)}
                                    className={`p-5 rounded-2xl border transition-all cursor-pointer group ${activeTopic === item.topic
                                        ? 'bg-[#2b2bee]/10 border-[#2b2bee] shadow-lg shadow-[#2b2bee]/5'
                                        : 'bg-slate-50 dark:bg-white/5 border-transparent hover:border-[#2b2bee]/30'}`}
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <h4 className="font-bold text-slate-800 dark:text-slate-200 group-hover:text-[#2b2bee] transition-colors line-clamp-2">
                                            {item.topic}
                                        </h4>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <StarIcon className="w-4 h-4 text-amber-500 fill-amber-500" />
                                            <span className="text-xs font-black text-slate-900 dark:text-white">{item.importanceScore}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="text-[10px] items-center flex gap-1 font-bold text-slate-400 uppercase">
                                                <CalendarIcon className="w-3 h-3" />
                                                Toplam: {item.totalLast7Years} Soru
                                            </div>
                                            <div className="text-[10px] items-center flex gap-1 font-bold text-[#2b2bee] uppercase">
                                                <ArrowTrendingUpIcon className="w-3 h-3" />
                                                Yıllık Ort: {item.average.toFixed(1)}
                                            </div>
                                        </div>
                                        <div className="w-16 h-1.5 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-[#2b2bee] rounded-full transition-all duration-1000"
                                                style={{ width: `${item.importanceScore}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Advisor Card */}
            {filteredData.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="p-10 rounded-[3rem] bg-slate-900 text-white relative overflow-hidden shadow-2xl mt-8"
                >
                    <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
                        <div className="w-24 h-24 bg-white/10 backdrop-blur-xl rounded-3xl flex items-center justify-center border border-white/10 shrink-0">
                            <LightBulbIcon className="w-12 h-12 text-amber-400" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 bg-amber-400/20 text-amber-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-amber-400/30">
                                    Supervisor AI
                                </span>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Canlı Analiz</span>
                            </div>
                            <h3 className="text-2xl font-black mb-4">Strateji Rehberi & Kişiselleştirilmiş Öneri</h3>
                            <p className="text-slate-400 text-lg leading-relaxed max-w-4xl italic font-medium">
                                "{selectedLesson} dersinde <span className="text-white font-bold">{filteredData[0]?.topic}</span> ve <span className="text-white font-bold">{filteredData[1]?.topic}</span> konuları {selectedExamType} başarının %30'unu oluşturuyor. Bu konuları tam hakimiyetle bitirmek seni rakiplerinin çok önüne taşıyacaktır."
                            </p>
                        </div>
                    </div>
                    <div className="absolute top-[-50%] left-[-10%] w-[60%] h-[200%] bg-gradient-to-r from-[#2b2bee]/20 to-transparent -rotate-12 blur-[100px] pointer-events-none" />
                </motion.div>
            )}
        </div>
    );
};
